/**
 * Real guest self-service API client — talks to /api/g/{slug}/privacy/*.
 *
 * ANONYMOUS but token-gated. Identity is proven by the single-use token from the
 * emailed link, which the page reads from the URL FRAGMENT and passes here; this
 * client sends it in the REQUEST BODY, never the query string (#121/#126) — the
 * same out-of-logs pattern as the shipped approval/moderate flow. request-link
 * always resolves neutrally (never reveals whether the email exists).
 *
 * Contract (token in body):
 *   POST   /request-link  { email }            -> 200 (neutral)
 *   POST   /me            { token }            -> flat { name, email, phone, consent, submissions }
 *   POST   /consent       { token, granted }   -> 200
 *   DELETE /me            { token }            -> 200
 * Invalid/expired tokens return 4xx (not 401), so guest routes never trip the
 * portal's login redirect (#131).
 */

import type { GuestSelfData, SelfServiceApi } from "../types";

async function requireOk(response: Response, what: string): Promise<Response> {
  if (!response.ok) {
    throw new Error(`${what} failed (${response.status})`);
  }
  return response;
}

function base(slug: string): string {
  return `/api/g/${encodeURIComponent(slug)}/privacy`;
}

const JSON_HEADERS = { "Content-Type": "application/json", Accept: "application/json" };

export const realSelfServiceApi: SelfServiceApi = {
  async requestLink(slug: string, email: string): Promise<void> {
    const response = await fetch(`${base(slug)}/request-link`, {
      method: "POST",
      headers: JSON_HEADERS,
      body: JSON.stringify({ email })
    });
    await requireOk(response, "Requesting your secure link");
  },

  async fetchMe(slug: string, token: string): Promise<GuestSelfData> {
    const response = await fetch(`${base(slug)}/me`, {
      method: "POST",
      headers: JSON_HEADERS,
      body: JSON.stringify({ token })
    });
    await requireOk(response, "Loading your info");
    return (await response.json()) as GuestSelfData;
  },

  async setConsent(slug: string, token: string, granted: boolean): Promise<void> {
    const response = await fetch(`${base(slug)}/consent`, {
      method: "POST",
      headers: JSON_HEADERS,
      body: JSON.stringify({ token, granted })
    });
    await requireOk(response, "Updating your consent");
  },

  async deleteMe(slug: string, token: string): Promise<void> {
    const response = await fetch(`${base(slug)}/me`, {
      method: "DELETE",
      headers: JSON_HEADERS,
      body: JSON.stringify({ token })
    });
    await requireOk(response, "Deleting your info");
  }
};
